import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Landmark, Users } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import Seo from "@/components/Seo";
import { PRICING, PRICING_NOTE, ROLE_RANGES, SETUP_FEES, MANAGED_TEAM_PRICING, INCLUDED_WITH_HIRE, ADD_ON } from "@/lib/content";

const Pricing = () => {
  const pricingJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Bright EOR — India Employer of Record",
    provider: { "@type": "Organization", name: "Bright EOR" },
    areaServed: "GB",
    offers: {
      "@type": "Offer",
      priceCurrency: "GBP",
      price: "895",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        priceCurrency: "GBP",
        price: "895",
        unitText: "MONTH",
        description: "Dedicated India-based professional, all-inclusive monthly price from £895.",
      },
    },
  };

  return (
    <>
      <Seo
        title="Pricing — One Clear Monthly Price | Bright EOR"
        description="Dedicated India-based professionals from £895/month, provided as one all-inclusive monthly figure. Transparent setup fees and managed-team pricing."
        path="/pricing"
        jsonLd={pricingJsonLd}
      />
      <PageHeader
        eyebrow="Pricing"
        title="One clear monthly price. No itemised breakdown."
        subtitle="Dedicated India-based professionals from £895/month, provided as a single all-inclusive figure covering the role, equipment, employment costs and managed support."
      />

      <section className="py-20 lg:py-28" data-testid="pricing-detail">
        <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
          <div className="grid gap-6 lg:grid-cols-2">
            {PRICING.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-3xl p-10 border transition-all ${tier.highlight ? "bg-navy text-white border-navy shadow-card-hover" : "bg-white border-slate-200 shadow-card-soft"}`}
                data-testid={`pricing-detail-${tier.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "")}`}
              >
                {tier.highlight && <div className="absolute top-6 right-6 rounded-full bg-aqua text-navy px-3 py-1 text-xs font-semibold">All-inclusive</div>}
                <h3 className={`font-display text-2xl font-semibold ${tier.highlight ? "text-white" : "text-navy"}`}>{tier.name}</h3>
                <p className={`mt-2 text-[14px] ${tier.highlight ? "text-white/70" : "text-slatemuted"}`}>{tier.subtitle}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className={`font-display text-5xl font-semibold ${tier.highlight ? "text-aqua" : "text-navy"}`}>{tier.price}</span>
                  <span className={`text-sm ${tier.highlight ? "text-white/70" : "text-slatemuted"}`}>{tier.unit}</span>
                </div>
                <ul className="mt-8 space-y-3">
                  {tier.includes.map((inc) => (
                    <li key={inc} className={`flex items-start gap-2.5 text-[14.5px] ${tier.highlight ? "text-white/90" : "text-slateink"}`}>
                      <Check className="h-4 w-4 text-aqua mt-0.5 shrink-0" strokeWidth={2.5} />{inc}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className={`group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-medium transition-all ${tier.highlight ? "bg-aqua text-navy hover:shadow-aqua-glow" : "bg-navy text-white hover:bg-aqua hover:text-navy"}`}>
                  Request an Indicative Cost <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            ))}
          </div>

          <p className="mt-8 text-[15px] text-slateink max-w-3xl leading-relaxed">{PRICING_NOTE}</p>

          {/* Indicative role ranges */}
          <div className="mt-16" data-testid="pricing-role-ranges">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Indicative role ranges</p>
            <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold text-navy tracking-tight">A rough guide by role type.</h3>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {ROLE_RANGES.map((r) => (
                <div key={r.label} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-card-soft">
                  <p className="font-display text-lg font-semibold text-navy tracking-tight">{r.label}</p>
                  <p className="mt-2 text-[15px] text-aqua font-semibold">{r.range}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-slatemuted">These are indicative examples, not guaranteed fixed prices.</p>
          </div>

          {/* Setup fees */}
          <div className="mt-16" data-testid="pricing-setup-fees">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">One-time setup</p>
            <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold text-navy tracking-tight">Setup depends on how you start.</h3>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {SETUP_FEES.map((f) => (
                <div key={f.label} className="rounded-2xl border border-slate-200 bg-navy-50 p-6">
                  <p className="text-[13.5px] font-medium text-slateink">{f.label}</p>
                  <p className="mt-3 font-display text-2xl font-semibold text-navy">{f.price}</p>
                  <p className="text-xs text-slatemuted mt-0.5">{f.unit}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Managed team pricing */}
          <div className="mt-16 grid gap-6 lg:grid-cols-2" data-testid="pricing-managed">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-card-soft">
              <div className="w-11 h-11 rounded-2xl bg-aqua/15 text-navy flex items-center justify-center"><Users className="h-5 w-5" /></div>
              <h3 className="font-display mt-5 text-xl font-semibold text-navy">Managed Team Pod</h3>
              <ul className="mt-5 space-y-3">
                {MANAGED_TEAM_PRICING.pod.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-[14.5px] text-slateink"><Check className="h-4 w-4 text-aqua mt-0.5 shrink-0" strokeWidth={2.5} />{p}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-navy text-white p-8">
              <div className="w-11 h-11 rounded-2xl bg-aqua text-navy flex items-center justify-center"><Landmark className="h-5 w-5" /></div>
              <h3 className="font-display mt-5 text-xl font-semibold">Managed Offshore Function</h3>
              <ul className="mt-5 space-y-3">
                {MANAGED_TEAM_PRICING.func.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-[14.5px] text-white/90"><Check className="h-4 w-4 text-aqua mt-0.5 shrink-0" strokeWidth={2.5} />{p}</li>
                ))}
              </ul>
              <p className="mt-6 text-[13.5px] text-white/60 leading-relaxed">{MANAGED_TEAM_PRICING.note}</p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-aqua hover:text-white transition-colors" data-testid="pricing-function-cta">
                Discuss a Managed Function <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Included with every hire */}
          <div className="mt-16 rounded-3xl bg-navy text-white p-8 lg:p-10 relative overflow-hidden" data-testid="pricing-included">
            <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
            <div className="absolute -top-40 -right-40 w-[400px] h-[400px] rounded-full bg-aqua/15 blur-3xl" aria-hidden />
            <div className="relative">
              <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Included with every hire</p>
              <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight">Everything baked into the monthly price.</h3>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {INCLUDED_WITH_HIRE.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-[14px] text-white/90"><Check className="h-4 w-4 text-aqua mt-0.5 shrink-0" strokeWidth={2.5} />{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Optional add-on */}
          <div className="mt-8 rounded-3xl border border-aqua/30 bg-aqua/5 p-8" data-testid="pricing-addon">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-aqua text-navy flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5" /></div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slatemuted font-semibold">Optional add-on</p>
                <h4 className="font-display mt-1 text-xl font-semibold text-navy">{ADD_ON.name}</h4>
                <p className="mt-2 text-[14px] text-slateink leading-relaxed">{ADD_ON.body}</p>
                <p className="mt-4 inline-flex items-baseline gap-1"><span className="font-display text-3xl font-semibold text-navy">{ADD_ON.price}</span><span className="text-sm text-slatemuted">{ADD_ON.unit}</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
};

export default Pricing;
